import { useEffect } from 'react';
import { Route, Routes, useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { Toaster } from 'react-hot-toast';
import Cookies from 'js-cookie';

import PrivateRoutesProtector from './router/PrivateRoutesProtector';
import AdminLayout from './Layout/AdminLayout/Index';
import AuthLayout from './Layout/AuthLayout';
import Login from './pages/Auth/Login.jsx';
import Signup from './pages/Auth/Signup';
import Home from './pages/Dashboard/Index';
import PurchaseOrders from './pages/Dashboard/orders/purchase';
import PurchaseOrderId from './pages/Dashboard/orders/purchase/_id';
import TransferOrders from './pages/Dashboard/orders/transfer';
import TransferOrderId from './pages/Dashboard/orders/transfer/_id';
import NotFound from './pages/Error/NotFound';
import UserManagement from './pages/Dashboard/users';
import Management from './pages/Dashboard/users/Management';
import SkuManagement from './pages/Dashboard/sku';
import Export from './pages/Dashboard/files/Export/index.jsx';
import Import from './pages/Dashboard/files/Import/index.jsx';
import FeedbackOrders from './pages/Dashboard/orders/feedback';
import VerificationPage from './pages/Auth/Verify';
import PurchaseOrderReceipts from './pages/Dashboard/orders/receipts/index.jsx';
import ChangePassword from './pages/Settings/ChangePassword.jsx';
import PurchaseReceiptId from './pages/Dashboard/orders/receipts/_id.jsx';
import Search from './pages/Dashboard/users/Search.jsx';

import LoaderBackdrop from './features/Loader/Loader';
import { loaderActions } from './features/Loader/LoaderSlice';
import { AuthActions } from './features/Auth/AuthSlice';
import { orderAction } from './features/Order/OrderSlice';
import { LoadFeedbackLoopStatus } from './features/General/GeneralSlice';
import VendorsManagement from './pages/Dashboard/vendors/index.jsx';

function App() {
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const loading = useSelector((state) => state.loader.open);
  const { fetchingOrder } = useSelector((state) => state.order);
  const { currentVendor } = useSelector((state) => state.vendor);
  const { isAuthenticated } = useSelector((state) => state.auth);
  const dispatch = useDispatch();

  useEffect(() => {
    if (loading) {
      dispatch(loaderActions.hide());
    }
    if (fetchingOrder) {
      dispatch(orderAction.resetOrderSlice());
    }
    if (!Cookies.get('expires_in')) {
      dispatch(AuthActions.logout());
    }
  }, []);

  useEffect(() => {
    const restrictedRoutesForSupplier = [
      '/transfer-orders',
      '/feedback-orders',
      '/purchase-receipts'
    ];
    if (Object.keys(currentVendor).length > 0) {
      if (currentVendor.VendorType && currentVendor.VendorType.toLowerCase() !== 'warehouse') {
        if (restrictedRoutesForSupplier.includes(location.pathname)) {
          if (location.pathname === '/feedback-orders') {
            if (!searchParams.get('vendorNo') || searchParams.get('vendorNo') === '') {
              navigate('/');
            }
          } else {
            navigate('/');
          }
        }
      }
    }
  }, [currentVendor.VendorType]);

  useEffect(() => {
    const version = '1.9.0';
    if (localStorage.getItem('version') && localStorage.getItem('version') !== version) {
      Object.keys(Cookies.get()).forEach(function (cookieName) {
        Cookies.remove(cookieName);
      });
      localStorage.clear();
    } else {
      localStorage.setItem('version', version);
    }
  }, []);

  useEffect(() => {
    if (isAuthenticated) {
      dispatch(LoadFeedbackLoopStatus());
    }
  }, [isAuthenticated]);

  return (
    <>
      {loading ? <LoaderBackdrop /> : <></>}
      <Toaster position="top-center" reverseOrder={false} gutter={8} />
      <Routes>
        <Route path="/" element={<PrivateRoutesProtector requiredAuth={true} />}>
          <Route path="" element={<AdminLayout />}>
            <Route path="" index element={<Home />} />
            <Route path="sku-breakdown" index element={<SkuManagement />} />
            <Route path="purchase-orders" element={<PurchaseOrders />} />
            <Route
              path="purchase-order/:vendorType/:id"
              name="purchase-order-id"
              element={<PurchaseOrderId />}
            />
            <Route path="transfer-orders" element={<TransferOrders />} />
            <Route
              path="transfer-order/:vendorType/:id"
              name="transfer-order-id"
              element={<TransferOrderId />}
            />
            <Route path="feedback-orders" element={<FeedbackOrders />} />
            <Route path="purchase-receipts" element={<PurchaseOrderReceipts />} />
            <Route path="purchase-receipt/:vendorType/:id" element={<PurchaseReceiptId />} />
            <Route path="users" element={<UserManagement />}>
              <Route path="management" index element={<Management />} />
              <Route path="search" index element={<Search />} />
            </Route>

            <Route path="file_management/export" element={<Export />} />
            <Route path="file_management/import" element={<Import />} />
            <Route path="settings/change-password" element={<ChangePassword />} />
            <Route path="vendor-management" element={<VendorsManagement />} />
          </Route>
        </Route>

        <Route path="/" element={<PrivateRoutesProtector requiredAuth={false} />}>
          <Route path="" element={<AuthLayout />}>
            <Route path="login" index element={<Login />} />
            <Route path="signup" element={<Signup />} />
            <Route path="verify" element={<VerificationPage />} />
          </Route>
        </Route>
        <Route path="*" element={<NotFound />} />
      </Routes>
    </>
  );
}

export default App;
